/**
 * The semantic token contract every interface theme must satisfy.
 *
 * This is the seam between messaging behaviour and messenger appearance.
 * Components never name a colour and never ask which theme is active; they use
 * a token. Adding a second theme later means writing one more object that fills
 * in these keys — not touching a component.
 *
 * Tokens are emitted as CSS custom properties under a neutral `--ui-` prefix —
 * deliberately not named after any one theme, because no value here belongs to
 * one — so CSS can consume them directly and a theme change is a single
 * attribute swap rather than a re-render of the tree.
 */

export interface InterfaceTokens {
  /* --- surfaces ------------------------------------------------------- */
  /** Behind everything; visible as the frame on wide screens. */
  appBackground: string;
  /** Conversation list column. */
  sidebarBackground: string;
  /** Sidebar and chat header bars. */
  headerBackground: string;
  /** The message area behind the bubbles. */
  chatBackground: string;
  /** Faint motif drawn over the chat background. Empty string disables it. */
  chatBackgroundPattern: string;
  /** Composer bar. */
  composerBackground: string;
  /** Inputs and raised panels. */
  inputBackground: string;
  /** Menus, dialogs and popovers. */
  overlayBackground: string;

  /* --- bubbles -------------------------------------------------------- */
  incomingBubble: string;
  incomingBubbleText: string;
  outgoingBubble: string;
  outgoingBubbleText: string;
  /** Quoted-message strip inside a bubble. */
  quoteBackground: string;
  quoteAccent: string;

  /* --- text ----------------------------------------------------------- */
  textPrimary: string;
  textSecondary: string;
  /** Timestamps and tick marks inside bubbles. */
  textMeta: string;
  textOnAccent: string;

  /* --- structure ------------------------------------------------------ */
  border: string;
  divider: string;
  icon: string;
  iconActive: string;

  /* --- interaction ---------------------------------------------------- */
  accent: string;
  accentHover: string;
  hover: string;
  selected: string;
  unreadBadge: string;
  unreadBadgeText: string;
  /** Read-receipt tick colour, where a theme shows one. */
  tickRead: string;
  systemBubble: string;
  systemBubbleText: string;
  danger: string;

  /* --- shape and type ------------------------------------------------- */
  fontFamily: string;
  bubbleRadius: string;
  /** Tighter corner where a bubble meets its tail. */
  bubbleTailRadius: string;
  avatarRadius: string;
  headerHeight: string;
  composerMinHeight: string;
  sidebarWidth: string;
  rowHeight: string;
  bubbleMaxWidth: string;
  bubbleShadow: string;
  panelShadow: string;
}

/** A theme supplies both colour schemes; the active one follows the device. */
export interface InterfaceTheme {
  id: string;
  label: string;
  /** Shown wherever the theme is described. */
  description: string;
  light: InterfaceTokens;
  dark: InterfaceTokens;
}

/** Converts tokens to the CSS custom properties the stylesheet consumes. */
export function tokensToCssVariables(tokens: InterfaceTokens): Record<string, string> {
  const entries = Object.entries(tokens).map(([key, value]) => [
    `--ui-${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`,
    value,
  ]);
  return Object.fromEntries(entries);
}
